// The Activity editor of a Weekly Timesheet — the bar ("Activity this
// week" + "Override a day") and the Split dialog (By worker / By percent).
// Lifted from js/app.js on 2026-10-19 so the foreman's phone and the admin's
// "Re-file hours" drawer run ONE editor on ONE arithmetic
// (timesheet-activity.js). Pure DOM over the `activity` object it is given:
// it mutates that object in place (that is what keeps the field's draft,
// auto-save and outbox untouched — they hold the same object) and tells the
// caller through onChange / onWeekPick; it never reads app state, storage or
// the network.
//
//   mountActivityBar(wrap, { tasks, activity, employees, onWeekPick, onChange })
//   openSplitDialog({ day, tasks, employees, activity, onClose })
//
// `tasks`     — [{ task_id, description }] of THIS job, in picker order
// `activity`  — { week: task_id | '' (General) | null (not chosen), days: {} }
// `employees` — the crew rows, in sheet order (overrides are keyed by index)

import { splitSeedRows, proportionalHours, byWorkerFallbackTask } from './timesheet-activity.js';

const TS_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const TS_DAY_FULL = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
                      fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
const r2 = v => Math.round((Number(v) || 0) * 100) / 100;

export function mountActivityBar(wrap, opts) {
  const { tasks, activity, employees, onWeekPick, onChange } = opts || {};
  wrap.innerHTML = '';
  if (!activity) return;
  if (!activity.days) activity.days = {};
  const remount = () => mountActivityBar(wrap, opts);

  const header = document.createElement('div');
  header.className = 'ts-activity-header';
  header.textContent = 'Activity this week';
  wrap.appendChild(header);

  // ── Week default ──
  const weekSel = document.createElement('select');
  weekSel.className = 'field-input ts-activity-week-select';
  weekSel.id = 'tsActivityWeek';
  const genOpt = document.createElement('option');
  genOpt.value = ''; genOpt.textContent = 'General';
  weekSel.appendChild(genOpt);
  tasks.forEach(t => {
    const o = document.createElement('option');
    o.value = t.task_id;
    o.textContent = t.description;
    weekSel.appendChild(o);
  });
  if (activity.week == null) {
    // Not chosen yet (2026-10-19, decision 2b-A): a job with two or more
    // tasks opens here, and step 3 refuses to sign until the foreman picks —
    // General included. A remembered pick or a one-task job never lands here.
    const ask = document.createElement('option');
    ask.value = '__choose__'; ask.textContent = 'Choose the activity…';
    weekSel.insertBefore(ask, genOpt);
    weekSel.value = '__choose__';
  } else {
    weekSel.value = tasks.some(t => t.task_id === activity.week) ? activity.week : '';
  }
  weekSel.addEventListener('change', e => {
    const v = e.target.value;
    if (v === '__choose__') { activity.week = null; onChange?.(); return; }
    activity.week = v;
    onWeekPick?.(activity.week);
    onChange?.();
  });
  wrap.appendChild(weekSel);

  // ── Per-day override strip ──
  const dayHeader = document.createElement('div');
  dayHeader.className = 'ts-activity-subheader';
  dayHeader.textContent = 'Override a day';
  wrap.appendChild(dayHeader);

  const grid = document.createElement('div');
  grid.className = 'ts-activity-days-grid';

  TS_DAYS.forEach(d => {
    const day = d.toLowerCase();
    const col = document.createElement('div');
    col.className = 'ts-activity-day-col';

    const lbl = document.createElement('div');
    lbl.className = 'ts-activity-day-label';
    lbl.textContent = d.toUpperCase();
    col.appendChild(lbl);

    const sel = document.createElement('select');
    sel.className = 'field-input ts-activity-day-select';

    const inheritOpt = document.createElement('option');
    // 'same' alone: the seven boxes are 39px wide on a 390px phone and
    // '↑ same' clipped to '↑ san' in every one of them.
    inheritOpt.value = '__inherit__'; inheritOpt.textContent = 'same';
    sel.appendChild(inheritOpt);

    const generalOpt = document.createElement('option');
    generalOpt.value = ''; generalOpt.textContent = 'General';
    sel.appendChild(generalOpt);

    tasks.forEach(t => {
      const o = document.createElement('option');
      o.value = t.task_id; o.textContent = t.description;
      sel.appendChild(o);
    });

    const cfg = activity.days[day];
    const splitOpt = document.createElement('option');
    splitOpt.value = '__split__';
    // A By-worker save always writes a single-entry `split` (the fallback for
    // a crew member added later) — counting its length would misleadingly
    // always read "(1)". A plain checkmark is accurate for both modes.
    splitOpt.textContent = cfg && Array.isArray(cfg.split) ? 'Split ✓' : 'Split…';
    sel.appendChild(splitOpt);

    let selVal = '__inherit__';
    if (cfg) {
      if (Array.isArray(cfg.split)) selVal = '__split__';
      else if ('task_id' in cfg) selVal = cfg.task_id || '';
    }
    sel.value = selVal;

    sel.addEventListener('change', e => {
      const v = e.target.value;
      if (v === '__split__') {
        openSplitDialog({ day, tasks, employees, activity,
          onClose: saved => { remount(); if (saved) onChange?.(); } });
        remount();   // put the select back until the dialog is saved
        return;
      }
      if (v === '__inherit__') delete activity.days[day];
      else activity.days[day] = { task_id: v };
      onChange?.();
    });

    col.appendChild(sel);
    grid.appendChild(col);
  });

  wrap.appendChild(grid);
}

// Seed rows for the Split dialog: the day's current split, else its current

// The Split dialog — a day's hours divided across two or more tasks. Two
// modes, a segmented toggle at the top:
//   "By worker"  (default for a fresh split) — one row per crew member, tap
//                a chip to send their whole day to that activity. No typing.
//                A "½" affordance expands a single row into +/− steppers for
//                the rare one-person split-their-own-day case.
//   "By percent" — the original crew-wide proportional split + optional
//                per-worker adjuster, unchanged, for a genuinely proportional
//                whole-crew split.
// Nothing here touches `activity` until Save; Cancel (or the backdrop, ✕ or
// Escape) discards the local working copy untouched. `onClose(saved)` fires
// once the overlay is gone, with true only after a Save.
export function openSplitDialog({ day, tasks, employees, activity, onClose }) {
  if (!activity) return;
  if (!activity.days) activity.days = {};
  const chipTasks = [{ task_id: '', description: 'General' }, ...tasks];
  const dayCfg = activity.days && activity.days[day];
  const weekTask = activity.week || '';

  // Crew who actually worked this day — sick or zero-hour rows don't need an
  // activity (matches the filter the old "Adjust per worker" list already
  // used, and what resolveWorkerDay/syncTimesheetToDb skip regardless).
  const emps = employees || [];
  const workable = [];
  emps.forEach((emp, idx) => {
    const cell = emp && emp.days && emp.days[day];
    const total = (parseFloat(cell && cell.r) || 0) + (parseFloat(cell && cell.ot) || 0);
    if (cell && !cell.sick && total > 0) workable.push({ idx, emp, total });
  });

  // ── By-percent state (unchanged shape from before this redesign) ──
  let rows = splitSeedRows(dayCfg, weekTask);
  const workerOverrides = {};
  Object.keys((dayCfg && dayCfg.workers) || {}).forEach(idx => {
    workerOverrides[idx] = (dayCfg.workers[idx] || [])
      .map(w => ({ task_id: w.task_id || '', hours: Number(w.hours) || 0 }));
  });
  let expanded = false;

  // ── By-worker state — ONE source of truth per row ──
  // rowEntries[idx] = { [task_id]: hours } — whatever's currently assigned
  // to that worker's day, full stop. Chip taps and stepper +/- BOTH edit
  // this same object directly; nothing else holds worker-split data. The
  // "½" toggle below only changes which EDITOR is visible for a row — it
  // never reads or rewrites rowEntries, so peeking at steppers and
  // collapsing back never loses or rewrites a single hour (the bug the
  // coordinator's review caught: the old design re-derived a fresh
  // "workerChip" on collapse, which silently discarded a real multi-task
  // split the moment a foreman tapped ½ twice).
  const rowEntries = {};
  const rowView = {};   // idx -> 'chip' | 'steppers' (a display PREFERENCE only)

  // A day holding a plain `{task_id}` override (picked directly from the
  // day-strip select, never split) is really "everyone on this one task" —
  // opening Split… on it should default every row to that task, not fall
  // through to the By-percent UI as if it were a proportional split.
  const isPlainTaskOverride = !!(dayCfg && !dayCfg.split && !dayCfg.workers && 'task_id' in dayCfg);
  const seedTask = isPlainTaskOverride ? (dayCfg.task_id || '') : (weekTask || '');

  workable.forEach(({ idx, total }) => {
    const entries = dayCfg && dayCfg.workers && dayCfg.workers[idx];
    rowEntries[idx] = {};
    if (entries && entries.length) {
      entries.forEach(e => { rowEntries[idx][e.task_id || ''] = r2(e.hours); });
    } else {
      rowEntries[idx][seedTask] = total;
    }
    rowView[idx] = 'chip';   // collapsed by default; render logic below upgrades
                              // to a read-only summary automatically when the
                              // row's data has more than one activity in it.
  });

  // Re-hydrate the right mode: every workable crew member already has a
  // per-worker entry, OR the day is a plain single-task override → By
  // worker (tap-first is the default, not the alternate path — matches a
  // brand-new split with no dayCfg at all, which also opens By worker).
  // Otherwise → By percent — covers a genuine % split and pre-redesign
  // partial per-worker data (both rendered by the untouched By-percent UI).
  const allHaveWorkers = workable.length > 0 && workable.every(({ idx }) =>
    dayCfg && dayCfg.workers && dayCfg.workers[idx] && dayCfg.workers[idx].length);
  let mode = (!dayCfg || isPlainTaskOverride || allHaveWorkers) ? 'byWorker' : 'byPercent';

  document.getElementById('tsSplitOverlay')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'tsSplitOverlay';
  overlay.className = 'ts-split-overlay';

  const bar = document.createElement('div');
  bar.className = 'ts-split-bar';
  const title = document.createElement('span');
  title.className = 'ts-split-title';
  title.textContent = 'Split ' + (TS_DAY_FULL[day] || day);
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'ts-split-close';
  closeBtn.textContent = '✕';
  closeBtn.setAttribute('aria-label', 'Cancel split');
  bar.appendChild(title);
  bar.appendChild(closeBtn);

  const body = document.createElement('div');
  body.className = 'ts-split-body';

  // ── Mode toggle ──
  const modeToggle = document.createElement('div');
  modeToggle.className = 'ts-split-mode-toggle';
  const modeWorkerBtn = document.createElement('button');
  modeWorkerBtn.type = 'button';
  modeWorkerBtn.className = 'ts-split-mode-btn';
  modeWorkerBtn.textContent = 'By worker';
  const modePctBtn = document.createElement('button');
  modePctBtn.type = 'button';
  modePctBtn.className = 'ts-split-mode-btn';
  modePctBtn.textContent = 'By percent';
  modeToggle.appendChild(modeWorkerBtn);
  modeToggle.appendChild(modePctBtn);
  body.appendChild(modeToggle);

  const byWorkerWrap = document.createElement('div');
  byWorkerWrap.className = 'ts-split-byworker';
  const pctModeWrap = document.createElement('div');
  pctModeWrap.className = 'ts-split-pctmode';
  body.appendChild(byWorkerWrap);
  body.appendChild(pctModeWrap);

  function updateModeToggle() {
    modeWorkerBtn.classList.toggle('active', mode === 'byWorker');
    modePctBtn.classList.toggle('active', mode === 'byPercent');
    byWorkerWrap.hidden = mode !== 'byWorker';
    pctModeWrap.hidden = mode !== 'byPercent';
  }
  modeWorkerBtn.addEventListener('click', () => { mode = 'byWorker'; updateModeToggle(); renderByWorkerRows(); });
  modePctBtn.addEventListener('click', () => { mode = 'byPercent'; updateModeToggle(); });

  // ── By worker: one row per crew member — chips, or steppers on demand ──
  function renderByWorkerRows() {
    byWorkerWrap.innerHTML = '';
    if (!workable.length) {
      const empty = document.createElement('div');
      empty.className = 'ts-split-worker-empty';
      empty.textContent = "No hours logged yet this day — fill in the crew's hours first.";
      byWorkerWrap.appendChild(empty);
      return;
    }
    workable.forEach(({ idx, emp, total }) => {
      const row = document.createElement('div');
      row.className = 'ts-split-byworker-row';

      const head = document.createElement('div');
      head.className = 'ts-split-byworker-head';
      const nm = document.createElement('span');
      nm.className = 'ts-split-byworker-name';
      nm.textContent = emp.name || `Employee ${idx + 1}`;
      const badge = document.createElement('span');
      badge.className = 'ts-split-byworker-badge';
      badge.textContent = `${total % 1 === 0 ? total : total.toFixed(1)}h`;
      const halfBtn = document.createElement('button');
      halfBtn.type = 'button';
      halfBtn.className = 'ts-split-byworker-half';
      halfBtn.textContent = '½';
      halfBtn.setAttribute('aria-label', `Split ${emp.name || 'this worker'}'s day across activities`);
      head.appendChild(nm); head.appendChild(badge); head.appendChild(halfBtn);
      row.appendChild(head);

      const chipsWrap = document.createElement('div');
      chipsWrap.className = 'ts-split-chips';
      const summaryWrap = document.createElement('div');
      summaryWrap.className = 'ts-split-byworker-summary';
      const stepWrap = document.createElement('div');
      stepWrap.className = 'ts-split-steppers';
      row.appendChild(chipsWrap);
      row.appendChild(summaryWrap);
      row.appendChild(stepWrap);
      byWorkerWrap.appendChild(row);

      // The only two questions any render needs answered, always computed
      // fresh off rowEntries[idx] — never cached, never a second variable
      // that could drift from it.
      const nonZeroEntries = () => Object.entries(rowEntries[idx]).filter(([, h]) => (Number(h) || 0) > 0);
      const fmt = v => (v % 1 === 0 ? String(v) : v.toFixed(1));
      const descOf = taskId => taskId ? ((tasks.find(t => t.task_id === taskId) || {}).description || 'General') : 'General';

      // Chips can only represent ONE activity. If the row's real data has
      // more than one (a multi-task split), collapsing must show a read-only
      // summary instead of chips — rendering chips there would force a
      // single-select choice onto data chips can't hold, which is exactly
      // how the previous design silently dropped a real split down to
      // "General" the instant a foreman collapsed the row without meaning
      // to touch anything.
      function displayMode() {
        if (rowView[idx] === 'steppers') return 'steppers';
        return nonZeroEntries().length > 1 ? 'summary' : 'chip';
      }

      function renderChips() {
        chipsWrap.innerHTML = '';
        const nz = nonZeroEntries();
        const activeId = nz.length === 1 ? nz[0][0] : '';
        chipTasks.forEach(t => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'ts-split-chip';
          chip.textContent = t.description;
          chip.classList.toggle('active', activeId === t.task_id);
          chip.addEventListener('click', () => {
            // A chip tap always means "this worker's whole day, one activity"
            // — replace the row's entries outright with exactly that.
            rowEntries[idx] = { [t.task_id]: total };
            renderChips();
          });
          chipsWrap.appendChild(chip);
        });
      }

      function renderSummary() {
        summaryWrap.innerHTML = '';
        const text = nonZeroEntries()
          .map(([taskId, h]) => `${fmt(Number(h) || 0)}h ${descOf(taskId)}`)
          .join(' · ');
        const span = document.createElement('span');
        span.className = 'ts-split-byworker-summary-text';
        // Non-interactive by construction — no click handler is ever
        // attached, so tapping this text can never rewrite rowEntries.
        span.textContent = text || 'No hours assigned yet';
        summaryWrap.appendChild(span);
      }

      function renderSteppers() {
        stepWrap.innerHTML = '';
        const sumEl = document.createElement('div');
        sumEl.className = 'ts-split-stepper-sum';
        const updateSumEl = () => {
          const sum = Object.values(rowEntries[idx]).reduce((s, h) => s + (Number(h) || 0), 0);
          const ok = Math.abs(sum - total) < 0.01;
          sumEl.textContent = `= ${fmt(sum)}h / ${fmt(total)}h`;
          sumEl.classList.toggle('ts-split-stepper-sum--ok', ok);
          sumEl.classList.toggle('ts-split-stepper-sum--bad', !ok);
        };
        chipTasks.forEach(t => {
          const key = t.task_id;
          const stRow = document.createElement('div');
          stRow.className = 'ts-split-stepper-row';
          const lbl = document.createElement('span');
          lbl.className = 'ts-split-stepper-lbl';
          lbl.textContent = t.description;
          const minus = document.createElement('button');
          minus.type = 'button'; minus.className = 'ts-split-stepper-btn'; minus.textContent = '−';
          minus.setAttribute('aria-label', 'Decrease ' + t.description);
          const val = document.createElement('span');
          val.className = 'ts-split-stepper-val';
          val.textContent = fmt(Number(rowEntries[idx][key]) || 0);
          const plus = document.createElement('button');
          plus.type = 'button'; plus.className = 'ts-split-stepper-btn'; plus.textContent = '+';
          plus.setAttribute('aria-label', 'Increase ' + t.description);
          // Edit rowEntries[idx] IN PLACE — this is the only place steppers
          // write, and it's the same object chips read/write too.
          minus.addEventListener('click', () => {
            const nv = Math.max(0, r2((Number(rowEntries[idx][key]) || 0) - 0.5));
            rowEntries[idx][key] = nv;
            val.textContent = fmt(nv);
            updateSumEl();
          });
          plus.addEventListener('click', () => {
            const nv = r2((Number(rowEntries[idx][key]) || 0) + 0.5);
            rowEntries[idx][key] = nv;
            val.textContent = fmt(nv);
            updateSumEl();
          });
          stRow.appendChild(lbl); stRow.appendChild(minus); stRow.appendChild(val); stRow.appendChild(plus);
          stepWrap.appendChild(stRow);
        });
        stepWrap.appendChild(sumEl);
        updateSumEl();
      }

      // Re-render whichever view the CURRENT data + preference call for.
      // This is the only place chipsWrap/summaryWrap/stepWrap visibility is
      // decided — always derived, never toggled directly by a caller.
      function updateRowMode() {
        const dm = displayMode();
        chipsWrap.hidden = dm !== 'chip';
        summaryWrap.hidden = dm !== 'summary';
        stepWrap.hidden = dm !== 'steppers';
        halfBtn.classList.toggle('active', dm === 'steppers');
        if (dm === 'chip') renderChips();
        else if (dm === 'summary') renderSummary();
        else renderSteppers();
      }
      // The ½ button ONLY flips a display preference — it never reads or
      // writes rowEntries. Collapsing out of steppers re-evaluates
      // displayMode() against whatever's actually in rowEntries right now:
      // one activity → chips (that one lit); two or more → the read-only
      // summary. Either way, not a single hour moves.
      halfBtn.addEventListener('click', () => {
        rowView[idx] = (rowView[idx] === 'steppers') ? 'chip' : 'steppers';
        updateRowMode();
      });

      updateRowMode();
    });
  }

  // ── By percent (existing crew-wide UI — logic untouched) ──
  const rowsWrap = document.createElement('div');
  rowsWrap.className = 'ts-split-rows';
  const sumLine = document.createElement('div');
  sumLine.className = 'ts-split-sum';
  const workerWrap = document.createElement('div');
  workerWrap.className = 'ts-split-workers';
  workerWrap.hidden = true;

  function updateSum() {
    const total = rows.reduce((s, r) => s + (Number(r.pct) || 0), 0);
    const ok = Math.abs(total - 100) < 0.01;
    sumLine.textContent = `= ${total}%`;
    sumLine.classList.toggle('ts-split-sum--ok', ok);
    sumLine.classList.toggle('ts-split-sum--bad', !ok);
  }

  function renderRows() {
    rowsWrap.innerHTML = '';
    rows.forEach((r, i) => {
      const rowEl = document.createElement('div');
      rowEl.className = 'ts-split-row';

      const sel = document.createElement('select');
      sel.className = 'field-input ts-split-task-select';
      const gOpt = document.createElement('option');
      gOpt.value = ''; gOpt.textContent = 'General';
      sel.appendChild(gOpt);
      tasks.forEach(t => {
        const o = document.createElement('option');
        o.value = t.task_id; o.textContent = t.description;
        sel.appendChild(o);
      });
      sel.value = tasks.some(t => t.task_id === r.task_id) ? r.task_id : '';
      sel.addEventListener('change', e => { r.task_id = e.target.value; renderWorkers(); });

      const pct = document.createElement('input');
      pct.type = 'number'; pct.min = '0'; pct.max = '100'; pct.step = '1';
      pct.inputMode = 'numeric';
      pct.className = 'field-input ts-split-pct-input';
      pct.value = r.pct;
      pct.addEventListener('input', e => { r.pct = e.target.value; updateSum(); renderWorkers(); });

      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'ts-split-row-remove';
      rm.textContent = '×';
      rm.setAttribute('aria-label', 'Remove task');
      rm.disabled = rows.length <= 1;
      rm.addEventListener('click', () => { rows.splice(i, 1); renderRows(); updateSum(); renderWorkers(); });

      rowEl.appendChild(sel); rowEl.appendChild(pct); rowEl.appendChild(rm);
      rowsWrap.appendChild(rowEl);
    });
  }

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'ts-split-add';
  addBtn.textContent = '+ Add task';
  addBtn.addEventListener('click', () => { rows.push({ task_id: '', pct: 0 }); renderRows(); updateSum(); renderWorkers(); });

  const expandBtn = document.createElement('button');
  expandBtn.type = 'button';
  expandBtn.className = 'ts-split-expand';
  expandBtn.textContent = '▸ Adjust per worker';
  expandBtn.addEventListener('click', () => {
    expanded = !expanded;
    expandBtn.textContent = (expanded ? '▾' : '▸') + ' Adjust per worker';
    workerWrap.hidden = !expanded;
    if (expanded) renderWorkers();
  });

  function renderWorkers() {
    if (!expanded) return;
    workerWrap.innerHTML = '';
    const emps = employees || [];
    let any = false;
    emps.forEach((emp, idx) => {
      const cell = emp && emp.days && emp.days[day];
      const total = (parseFloat(cell && cell.r) || 0) + (parseFloat(cell && cell.ot) || 0);
      if (!cell || cell.sick || total <= 0) return;
      any = true;

      const card = document.createElement('div');
      card.className = 'ts-split-worker';

      const head = document.createElement('div');
      head.className = 'ts-split-worker-head';
      const nm = document.createElement('span');
      nm.className = 'ts-split-worker-name';
      nm.textContent = emp.name || `Employee ${idx + 1}`;
      const tot = document.createElement('span');
      tot.className = 'ts-split-worker-total';
      head.appendChild(nm); head.appendChild(tot);
      card.appendChild(head);

      const boxesWrap = document.createElement('div');
      boxesWrap.className = 'ts-split-worker-boxes';

      const getVal = taskId => {
        const ov = workerOverrides[idx];
        if (ov) { const m = ov.find(w => w.task_id === taskId); return m ? m.hours : 0; }
        return proportionalHours(total, rows, taskId);
      };
      const updateWorkerTotal = () => {
        const sum = (workerOverrides[idx] || rows.map(r => ({ hours: proportionalHours(total, rows, r.task_id || '') })))
          .reduce((s, w) => s + (Number(w.hours) || 0), 0);
        const ok = Math.abs(sum - total) < 0.01;
        tot.textContent = `${sum % 1 === 0 ? sum : sum.toFixed(1)}h / ${total % 1 === 0 ? total : total.toFixed(1)}h`;
        tot.classList.toggle('ts-split-worker-total--ok', ok);
        tot.classList.toggle('ts-split-worker-total--bad', !ok);
      };
      const setVal = (taskId, hrs) => {
        if (!workerOverrides[idx]) {
          workerOverrides[idx] = rows.map(r => ({
            task_id: r.task_id || '', hours: proportionalHours(total, rows, r.task_id || '') }));
        }
        const entry = workerOverrides[idx].find(w => w.task_id === taskId);
        if (entry) entry.hours = hrs; else workerOverrides[idx].push({ task_id: taskId, hours: hrs });
        updateWorkerTotal();
      };

      rows.forEach(r => {
        const box = document.createElement('div');
        box.className = 'ts-split-worker-box';
        const taskId = r.task_id || '';
        const lbl = document.createElement('span');
        lbl.className = 'ts-split-worker-box-lbl';
        lbl.textContent = taskId ? ((tasks.find(t => t.task_id === taskId) || {}).description || 'General') : 'General';
        const inp = document.createElement('input');
        inp.type = 'number'; inp.min = '0'; inp.step = '0.5';
        inp.inputMode = 'decimal';
        inp.className = 'field-input ts-split-worker-input';
        inp.value = getVal(taskId);
        inp.addEventListener('input', e => setVal(taskId, parseFloat(e.target.value) || 0));
        box.appendChild(lbl); box.appendChild(inp);
        boxesWrap.appendChild(box);
      });
      card.appendChild(boxesWrap);
      workerWrap.appendChild(card);
      updateWorkerTotal();
    });
    if (!any) {
      const empty = document.createElement('div');
      empty.className = 'ts-split-worker-empty';
      empty.textContent = "No hours logged yet this day — fill in the crew's hours first.";
      workerWrap.appendChild(empty);
    }
  }

  renderRows();
  updateSum();

  pctModeWrap.appendChild(rowsWrap);
  pctModeWrap.appendChild(sumLine);
  pctModeWrap.appendChild(addBtn);
  pctModeWrap.appendChild(expandBtn);
  pctModeWrap.appendChild(workerWrap);

  // Mode starts wherever the hydration above landed; build the visible side.
  updateModeToggle();
  if (mode === 'byWorker') renderByWorkerRows();

  const footer = document.createElement('div');
  footer.className = 'ts-split-footer';
  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'ts-split-cancel';
  cancelBtn.textContent = 'Cancel';
  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.className = 'ts-split-save';
  saveBtn.textContent = 'Save split';
  footer.appendChild(cancelBtn);
  footer.appendChild(saveBtn);

  function close(saved) {
    document.removeEventListener('keydown', onKey, true);
    overlay.remove();
    if (onClose) onClose(!!saved);
  }
  // Escape closes THIS dialog and nothing else. In the admin the shell's own
  // Escape listener (bubble phase, on document) would otherwise shut the
  // drawer underneath the sheet; capture phase runs first and stops it there.
  const onKey = e => {
    if (e.key !== 'Escape') return;
    e.stopImmediatePropagation(); e.preventDefault();
    close(false);
  };
  document.addEventListener('keydown', onKey, true);
  closeBtn.addEventListener('click', () => close(false));
  cancelBtn.addEventListener('click', () => close(false));
  overlay.addEventListener('click', e => { if (e.target === overlay) close(false); });
  saveBtn.addEventListener('click', () => {
    if (mode === 'byWorker') {
      // Serialize rowEntries verbatim — the single source of truth every
      // editor wrote to directly. Dropping zero-hour entries happens ONLY
      // here, at Save; it never happens mid-edit, so nothing is silently
      // discarded while a foreman is still peeking at a row.
      const workersOut = {};
      workable.forEach(({ idx }) => {
        const entries = Object.entries(rowEntries[idx])
          .filter(([, h]) => (Number(h) || 0) > 0)
          .map(([taskId, h]) => ({ task_id: taskId, hours: r2(h) }));
        workersOut[idx] = entries.length ? entries : [{ task_id: '', hours: 0 }];
      });
      activity.days[day] = {
        // Fallback ONLY — for a crew member added to the timesheet AFTER this
        // split was saved. resolveWorkerDay finds no `workers[idx]` entry for
        // a new hire and falls through to this single 100%-weight split
        // entry instead of failing or defaulting to General.
        split: [{ task_id: byWorkerFallbackTask(rowEntries, weekTask), pct: 100 }],
        workers: workersOut,
      };
    } else {
      const split = rows.map(r => ({ task_id: r.task_id || '', pct: Number(r.pct) || 0 }));
      const dayCfgOut = { split };
      const workersOut = {};
      Object.keys(workerOverrides).forEach(idx => {
        const arr = (workerOverrides[idx] || []).filter(w => (Number(w.hours) || 0) > 0);
        if (arr.length) workersOut[idx] = arr;
      });
      if (Object.keys(workersOut).length) dayCfgOut.workers = workersOut;
      activity.days[day] = dayCfgOut;
    }
    close(true);
  });

  overlay.appendChild(bar);
  overlay.appendChild(body);
  overlay.appendChild(footer);
  document.body.appendChild(overlay);
}
